import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Cart, CartItem, Product } from '../types';

interface CartStore extends Cart {
  addItem: (product: Product, size: string, quantity?: number) => void;
  removeItem: (productId: string, size: string) => void;
  updateQuantity: (productId: string, size: string, quantity: number) => void;
  clearCart: () => void;
  getItemQuantity: (productId: string, size: string) => number;
  calculateTotal: () => void;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      total: 0,
      itemCount: 0,

      addItem: (product: Product, size: string, quantity = 1) => {
        const { items } = get();
        const existingItemIndex = items.findIndex(
          item => item.product.id === product.id && item.size === size
        );

        if (existingItemIndex > -1) {
          // Update existing item
          const updatedItems = items.map((item, index) =>
            index === existingItemIndex
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
          set({ items: updatedItems });
        } else {
          // Add new item
          const newItem: CartItem = {
            product,
            quantity,
            size,
            price: product.price,
          };
          set({ items: [...items, newItem] });
        }

        get().calculateTotal();
      },

      removeItem: (productId: string, size: string) => {
        const { items } = get();
        const updatedItems = items.filter(
          item => !(item.product.id === productId && item.size === size)
        );
        set({ items: updatedItems });
        get().calculateTotal();
      },

      updateQuantity: (productId: string, size: string, quantity: number) => {
        if (quantity <= 0) {
          get().removeItem(productId, size);
          return;
        }

        const { items } = get();
        const updatedItems = items.map(item =>
          item.product.id === productId && item.size === size
            ? { ...item, quantity }
            : item
        );
        set({ items: updatedItems });
        get().calculateTotal();
      },

      clearCart: () => {
        set({ items: [], total: 0, itemCount: 0 });
      },

      getItemQuantity: (productId: string, size: string) => {
        const { items } = get();
        const item = items.find(
          item => item.product.id === productId && item.size === size
        );
        return item ? item.quantity : 0;
      },

      calculateTotal: () => {
        const { items } = get();
        const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
        set({ total, itemCount });
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);
